import { useQuery } from "@tanstack/react-query";
import type { StaffPermission } from "@shared/schema";
import { useAuth } from "./auth";

export function usePermissions(shopId?: number) {
  const { user } = useAuth();

  const isOwnerOrAdmin = user?.role === "shop_owner" || user?.role === "admin";

  const { data: staffList } = useQuery<any[]>({
    queryKey: ["/api/shops", shopId, "staff"],
    enabled: !!shopId && !!user && !isOwnerOrAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Find the staff record for the current user in this shop
  const currentStaff = staffList?.find((s) => s.userId === user?.id);

  const { data: permissions, isLoading: permsLoading } = useQuery<StaffPermission[]>({
    queryKey: ["/api/shops", shopId, "staff", currentStaff?.id, "permissions"],
    enabled: !!shopId && !!currentStaff?.id && !isOwnerOrAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = !isOwnerOrAdmin && (!staffList || permsLoading);

  const hasPermission = (area: string, action: "view" | "add" | "edit" | "delete" = "view") => {
    // Shop owners and admins have all permissions — no need to wait for query
    if (isOwnerOrAdmin) return true;

    // While loading, show all items to avoid blank sidebar flash
    if (isLoading) return true;

    if (!permissions) return false;
    const perm = permissions.find((p) => p.area === area);
    if (!perm) return false;

    switch (action) {
      case "view": return perm.canView;
      case "add": return perm.canAdd;
      case "edit": return perm.canEdit;
      case "delete": return perm.canDelete;
      default: return false;
    }
  };

  return {
    permissions,
    isLoading,
    hasPermission,
  };
}
